import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import LandingPage from '@/pages/LandingPage';
import AuthPage from '@/pages/AuthPage';
import ForgotPassword from '@/pages/ForgotPassword';
import TutorDashboard from '@/pages/TutorDashboard';
import StudentDashboard from '@/pages/StudentDashboard';
import TutorProfile from '@/pages/TutorProfile';
import TutorProfileEdit from '@/pages/TutorProfileEdit';
import BrowseTutors from '@/pages/BrowseTutors';
import WalletPage from '@/pages/WalletPage';
import JobsPage from '@/pages/JobsPage';
import MessagesPage from '@/pages/MessagesPage';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import TermsConditions from '@/pages/TermsConditions';
import RefundPolicy from '@/pages/RefundPolicy';
import ContactUs from '@/pages/ContactUs';
import StudentsLandingPage from '@/pages/StudentsLandingPage';
import TutorsLandingPage from '@/pages/TutorsLandingPage';
import AdminReports from '@/pages/AdminReports';
import AdminTutors from '@/pages/AdminTutors';
import AdminLeads from '@/pages/AdminLeads';
import AdminStudents from '@/pages/AdminStudents';
import { api, AUTH_USER_UPDATED_EVENT, setStoredUser } from '@/utils/api';
import { useState, useEffect } from 'react';
import { captureAttributionFromLocation, trackPageView } from '@/lib/metaPixel';
import './App.css';

const PrivateRoute = ({ children, allowedRoles, user, loading }) => {
  if (loading) return <div>Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }
  return children;
};

const AuthRoute = ({ children, user, loading }) => {
  if (loading) return <div>Loading...</div>;
  if (user) {
    const redirectPath = user.role === 'tutor' ? '/tutor/dashboard' : '/student/dashboard';
    return <Navigate to={redirectPath} replace />;
  }
  return children;
};

const RouteAnalytics = () => {
  const location = useLocation();

  useEffect(() => {
    captureAttributionFromLocation(location.search);
    trackPageView({
      page_path: location.pathname,
      page_search: location.search,
    });
  }, [location.pathname, location.search]);

  return null;
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const syncUserFromStorage = () => {
      const token = localStorage.getItem('token');
      const userData = localStorage.getItem('user');
      if (token && userData) {
        try {
          setUser(JSON.parse(userData));
        } catch (_err) {
          setUser(null);
        }
      } else {
        setUser(null);
      }
    };

    const syncUserFromApi = async () => {
      const token = localStorage.getItem('token');
      const userData = localStorage.getItem('user');
      if (!token || !userData) return;
      try {
        const localUser = JSON.parse(userData);
        const response = await api.get('/me');
        const mergedUser = {
          ...localUser,
          ...response.data,
          coins: response.data?.coins ?? localUser?.coins ?? 0
        };
        setStoredUser(mergedUser);
      } catch (_err) {
        // Ignore sync errors; auth interceptor will handle invalid sessions.
      }
    };

    syncUserFromStorage();
    syncUserFromApi();
    setLoading(false);

    window.addEventListener(AUTH_USER_UPDATED_EVENT, syncUserFromStorage);
    window.addEventListener('storage', syncUserFromStorage);
    window.addEventListener('focus', syncUserFromApi);
    const intervalId = setInterval(syncUserFromApi, 30000);
    return () => {
      window.removeEventListener(AUTH_USER_UPDATED_EVENT, syncUserFromStorage);
      window.removeEventListener('storage', syncUserFromStorage);
      window.removeEventListener('focus', syncUserFromApi);
      clearInterval(intervalId);
    };
  }, []);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <div className="App">
      <div className="noise-overlay" />
      <BrowserRouter>
        <RouteAnalytics />
        <Routes>
          <Route path="/" element={<LandingPage user={user} />} />
          <Route path="/for-students" element={<StudentsLandingPage user={user} />} />
          <Route path="/for-tutors" element={<TutorsLandingPage user={user} />} />
          <Route
            path="/auth"
            element={
              <AuthRoute user={user} loading={loading}>
                <AuthPage setUser={setUser} />
              </AuthRoute>
            }
          />
          <Route
            path="/login"
            element={
              <AuthRoute user={user} loading={loading}>
                <AuthPage setUser={setUser} />
              </AuthRoute>
            }
          />
          <Route
            path="/signup"
            element={
              <AuthRoute user={user} loading={loading}>
                <AuthPage setUser={setUser} />
              </AuthRoute>
            }
          />
          <Route path="/tutor/signup" element={<Navigate to="/auth" replace />} />
          <Route path="/student/signup" element={<Navigate to="/auth" replace />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route
            path="/tutor/dashboard"
            element={
              <PrivateRoute allowedRoles={['tutor']} user={user} loading={loading}>
                <TutorDashboard user={user} setUser={setUser} />
              </PrivateRoute>
            }
          />
          <Route
            path="/tutor/profile/edit"
            element={
              <PrivateRoute allowedRoles={['tutor']} user={user} loading={loading}>
                <TutorProfileEdit user={user} />
              </PrivateRoute>
            }
          />
          <Route
            path="/student/dashboard"
            element={
              <PrivateRoute allowedRoles={['student', 'parent', 'coaching', 'company']} user={user} loading={loading}>
                <StudentDashboard user={user} setUser={setUser} />
              </PrivateRoute>
            }
          />
          <Route path="/tutors" element={<BrowseTutors user={user} />} />
          <Route path="/tutors/:tutorId" element={<TutorProfile user={user} />} />
          <Route
            path="/wallet"
            element={
              <PrivateRoute user={user} loading={loading}>
                <WalletPage user={user} setUser={setUser} />
              </PrivateRoute>
            }
          />
          <Route
            path="/jobs"
            element={
              <PrivateRoute allowedRoles={['tutor']} user={user} loading={loading}>
                <JobsPage user={user} setUser={setUser} />
              </PrivateRoute>
            }
          />
          <Route
            path="/messages"
            element={
              <PrivateRoute user={user} loading={loading}>
                <MessagesPage user={user} />
              </PrivateRoute>
            }
          />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-conditions" element={<TermsConditions />} />
          <Route path="/refund-policy" element={<RefundPolicy />} />
          <Route path="/contact-us" element={<ContactUs />} />
          <Route path="/admin" element={<AdminReports />} />
          <Route path="/admin/tutors" element={<AdminTutors />} />
          <Route path="/admin/students" element={<AdminStudents />} />
          <Route path="/admin/leads" element={<AdminLeads />} />
          <Route path="/admin/reports" element={<Navigate to="/admin" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;
